import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useForwardedRef, useResizeObserver } from '../../../hooks';
import { toId } from '../../../utils';
import { Button } from '../../Button';
import { Heading } from '../../Heading';
import { IconButton } from '../../IconButton';
import { LayoutStack } from '../LayoutStack';
import styles from './LayoutModal.module.css';
import { LayoutModalProps } from './types';
function safeCloseDialogElement(element?: HTMLDialogElement | null): void {
  if (!element) return;
  if (!('close' in element) || typeof element.close !== 'function') return;
  element.close();
}
const LayoutModal = forwardRef<HTMLDialogElement, LayoutModalProps>(function LayoutModal(t0, fwdRef) {
  const $ = _c(74);
  const {
    id,
    isOpen,
    onClose,
    onOpen,
    onCloseComplete: onCloseComplete_,
    onOpenComplete: onOpenComplete_,
    title,
    customActions,
    children,
    manualFocusRef,
    closeText,
    closeOnClickOutside: t1,
    hideHeader: t2,
    hideActions: t3,
    classNames: t4
  } = t0;
  const closeOnClickOutside = t1 === undefined ? true : t1;
  const hideHeader = t2 === undefined ? false : t2;
  const hideActions = t3 === undefined ? false : t3;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? {} : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const externalStyles = t5;
  const containerRef = useRef(null);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      ref: containerRef,
      box: "border-box"
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    height
  } = useResizeObserver(t6);
  const modalRef = useForwardedRef(fwdRef);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const defaultFocusRef = useRef(null);
  const [animationCompleted, setAnimationCompleted] = useState(true);
  let t7;
  if ($[3] !== manualFocusRef || $[4] !== modalRef) {
    t7 = () => {
      if (manualFocusRef) {
        return manualFocusRef;
      }
      if (defaultFocusRef.current) {
        return defaultFocusRef;
      }
      return modalRef;
    };
    $[3] = manualFocusRef;
    $[4] = modalRef;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const getFocusRef = t7;
  let t8;
  if ($[6] !== id || $[7] !== title) {
    t8 = id || toId(title);
    $[6] = id;
    $[7] = title;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const modalId = t8;
  let t9;
  if ($[9] !== modalRef || $[10] !== onClose) {
    t9 = event => {
      if (event.key === "Escape") {
        return onClose(event);
      }
      if (event.key !== "Tab") {
        return;
      }
      const focusableElements = modalRef.current?.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
      const firstElement = focusableElements?.[0] as HTMLElement;
      const lastElement = focusableElements?.[focusableElements.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
        return;
      }
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };
    $[9] = modalRef;
    $[10] = onClose;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleKeyPressed = t9;
  let t10;
  if ($[12] !== modalRef || $[13] !== onCloseComplete_) {
    t10 = event_0 => {
      if (event_0 != null && (event_0.target !== modalRef.current || event_0.propertyName !== "opacity")) {
        return;
      }
      onCloseComplete_?.();
      setAnimationCompleted(true);
    };
    $[12] = modalRef;
    $[13] = onCloseComplete_;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const onCloseComplete = t10;
  let t11;
  if ($[15] !== modalRef || $[16] !== onOpenComplete_) {
    t11 = event_1 => {
      if (event_1 != null && (event_1.target !== modalRef.current || event_1.propertyName !== "opacity")) {
        return;
      }
      onOpenComplete_?.();
      setAnimationCompleted(true);
      modalRef.current?.removeAttribute("inert");
    };
    $[15] = modalRef;
    $[16] = onOpenComplete_;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const onOpenComplete = t11;
  let t12;
  let t13;
  if ($[18] !== getFocusRef || $[19] !== handleKeyPressed || $[20] !== isOpen || $[21] !== modalRef || $[22] !== onCloseComplete || $[23] !== onOpen || $[24] !== onOpenComplete) {
    t12 = () => {
      const modalElement = modalRef.current;
      const hasReducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches || false;
      if (!modalElement) {
        return;
      }
      if (isOpen) {
        ;
        try {
          modalElement.showModal();
        } catch (t14) {
          const e = t14;
          console.error(e);
        }
        const focusRef = getFocusRef();
        focusRef?.current?.focus();
        if (hasReducedMotion) {
          setTimeout(onOpenComplete, 0);
        }
        modalElement.removeEventListener("transitionend", onCloseComplete);
        modalElement.addEventListener("keydown", handleKeyPressed);
        modalElement.addEventListener("transitionend", onOpenComplete);
        onOpen?.();
      } else {
        if (hasReducedMotion) {
          setTimeout(onCloseComplete, 0);
        }
        modalElement.removeEventListener("keydown", handleKeyPressed);
        modalElement.removeEventListener("transitionend", onOpenComplete);
        modalElement.addEventListener("transitionend", onCloseComplete);
        safeCloseDialogElement(modalElement);
        modalElement.setAttribute("inert", "");
      }
      return () => {
        if (!modalElement) {
          return;
        }
        modalElement.removeEventListener("keydown", handleKeyPressed);
        modalElement.removeEventListener("transitionend", onCloseComplete);
        modalElement.removeEventListener("transitionend", onOpenComplete);
      };
    };
    t13 = [getFocusRef, handleKeyPressed, isOpen, modalRef, onCloseComplete, onOpen, onOpenComplete];
    $[18] = getFocusRef;
    $[19] = handleKeyPressed;
    $[20] = isOpen;
    $[21] = modalRef;
    $[22] = onCloseComplete;
    $[23] = onOpen;
    $[24] = onOpenComplete;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  if ($[27] !== modalRef || $[28] !== onClose) {
    t14 = event_2 => {
      const {
        current: el
      } = modalRef;
      if (event_2.target === el) {
        onClose(event_2);
      }
    };
    $[27] = modalRef;
    $[28] = onClose;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const handleClickOutside = t14;
  const shouldShowContent = isOpen || !animationCompleted || prevIsOpen !== isOpen;
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setAnimationCompleted(false);
  }
  const headerId = `modal-title-${modalId}`;
  const ariaLabelledBy = !hideHeader ? headerId : undefined;
  let t15;
  if ($[30] !== externalStyles.modal) {
    t15 = classNames(styles.modal, externalStyles.modal);
    $[30] = externalStyles.modal;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = closeOnClickOutside ? handleClickOutside : undefined;
  const t17 = `${height}px`;
  let t18;
  if ($[32] !== t17) {
    t18 = {
      "--height": t17
    };
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = t18 as CSSProperties;
  let t20;
  if ($[34] !== externalStyles.modalInner) {
    t20 = classNames(styles.modalInner, externalStyles.modalInner);
    $[34] = externalStyles.modalInner;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== closeText || $[37] !== externalStyles.titleContainer || $[38] !== headerId || $[39] !== hideHeader || $[40] !== onClose || $[41] !== title) {
    t21 = !hideHeader && <div className={classNames(styles.modalHeader, externalStyles.titleContainer)}><Heading level="heading-sm" as="h2" id={headerId}>{title}</Heading><IconButton kind="plain" className={styles.closeButton} onClick={onClose} ref={defaultFocusRef} type="button" iconName="x" iconProps={{
        title: closeText,
        color: "fg-content-primary"
      }} /></div>;
    $[36] = closeText;
    $[37] = externalStyles.titleContainer;
    $[38] = headerId;
    $[39] = hideHeader;
    $[40] = onClose;
    $[41] = title;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== externalStyles.contentContainer) {
    t22 = classNames(styles.modalContent, externalStyles.contentContainer);
    $[43] = externalStyles.contentContainer;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const t23 = shouldShowContent && children;
  let t24;
  if ($[45] !== t22 || $[46] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== closeText || $[49] !== customActions || $[50] !== externalStyles.actionsContainer || $[51] !== hideActions || $[52] !== onClose) {
    t25 = !hideActions && <div className={classNames(styles.modalActions, externalStyles.actionsContainer)}>{!customActions ? <Button onClick={onClose} type="button">{closeText}</Button> : customActions}</div>;
    $[48] = closeText;
    $[49] = customActions;
    $[50] = externalStyles.actionsContainer;
    $[51] = hideActions;
    $[52] = onClose;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t20 || $[55] !== t21 || $[56] !== t24 || $[57] !== t25) {
    t26 = <LayoutStack space="400" className={t20} ref={containerRef}>{t21}{t24}{t25}</LayoutStack>;
    $[54] = t20;
    $[55] = t21;
    $[56] = t24;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== isOpen) {
    t27 = isOpen && <><div id="bp-portal-target" className={styles.portalTarget} role="alert" /><div id="bp-portal-target-inert" className={styles.portalTargetInert} role="alert" /></>;
    $[59] = isOpen;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t19 || $[62] !== t26 || $[63] !== t27) {
    t28 = <LayoutStack space="none" className={styles.outerContainer} style={t19}>{t26}{t27}</LayoutStack>;
    $[61] = t19;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== ariaLabelledBy || $[66] !== modalId || $[67] !== modalRef || $[68] !== onClose || $[69] !== t15 || $[70] !== t16 || $[71] !== t28 || $[72] !== title) {
    t29 = <dialog id={modalId} ref={modalRef} className={t15} onCancel={onClose} onClick={t16} aria-labelledby={ariaLabelledBy} aria-modal="true" title={title} tabIndex={-1}>{t28}</dialog>;
    $[65] = ariaLabelledBy;
    $[66] = modalId;
    $[67] = modalRef;
    $[68] = onClose;
    $[69] = t15;
    $[70] = t16;
    $[71] = t28;
    $[72] = title;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  return t29;
});
export default LayoutModal;